import { createRSOApi, updateRSOApi } from "@/api/services/admin/rso";
import { Form } from "@/components/elements/Form";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { schema } from "@/features/rso/schema/Schema";
import { TPartnerForm } from "@/features/rso/types/partnerForm";
import { convertPartnerFormToCreateReq, convertPartnerFormToUpdateReq } from "@/features/rso/utils/converts";
import { useToast } from "@/hooks/useToast";
import { useTriggerValidate } from "@/hooks/useTriggerValidate";
import { AppRoutes } from "@/routes/config";
import { setErrorForm, showError } from "@/utils/error";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { BasicForm } from "@/features/rso/components/Form/BasicForm";
import { ContractForm } from "@/features/rso/components/Form/ContractForm";
import { OtherInfoForm } from "@/features/rso/components/Form/OtherInfoForm";
import { Button } from "@/components/elements/Button";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { ERSOContractStatus } from "@/types/enum";
import { getDynamicRoute } from "@/utils/url";
import { text } from "@/theme/colors";
import { Typography } from "@mui/material";
import { useWatch } from "react-hook-form";
import SaveIcon from "@mui/icons-material/Save";

type TManagementFormProps = {
  previousData?: Partial<TPartnerForm>;
};

const initCreateForm = {
  id: null,
  name: "",
  licenseNumber: "",
  contractStatus: "",
  contractPlan: "",
  description: "",
  note: "",
};

export const ManagementForm = forwardRef<{ submit: () => void }, TManagementFormProps>(({ previousData }, ref) => {
  const { id } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const initialValues = useMemo(() => ({ ...initCreateForm, ...previousData, contractStatus: previousData?.contractStatus ?? "" }), [previousData]);
  const methods = useForm<TPartnerForm, object, TPartnerForm>({
    defaultValues: initialValues,
    resolver: yupResolver<TPartnerForm>(schema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const [loading, setLoading] = useState(false);
  useTriggerValidate({ methods, name: "contractStatus", triggerFields: ["trialEndDate", "contractStartDate"] });
  useTriggerValidate({ methods, name: "contractStartDate", triggerFields: ["contractStatus", "trialEndDate", "contractEndDate"] });
  useTriggerValidate({ methods, name: "trialStartDate", triggerFields: ["trialEndDate", "contractStartDate", "contractStatus"] });
  useTriggerValidate({ methods, name: "trialEndDate", triggerFields: ["trialStartDate", "contractStartDate", "contractStatus"] });
  useTriggerValidate({ methods, name: "contractEndDate", triggerFields: ["contractStatus"] });

  const onSubmit = async (data: TPartnerForm) => {
    setLoading(true);
    try {
      const finalData: TPartnerForm = {
        ...data,
      };

      if (id) {
        const payload = convertPartnerFormToUpdateReq(finalData);
        const rs = await updateRSOApi(id, payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
        navigate(getDynamicRoute(AppRoutes.rsoDetail, { id }));
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RSO.FETCH_DETAIL, id] });
      } else {
        const payload = convertPartnerFormToCreateReq(finalData);
        const rs = await createRSOApi(payload);
        if (payload.contractStatus === ERSOContractStatus.DURING_CONTRACT) {
          navigate(AppRoutes.duringContractList);
        }
        if (payload.contractStatus === ERSOContractStatus.CANCELED_CONTRACT) {
          navigate(AppRoutes.canceledContractList);
        }
        if (payload.contractStatus === ERSOContractStatus.TRIAL_CANCELLED) {
          navigate(AppRoutes.trialCancelledList);
        }
        if (payload.contractStatus === ERSOContractStatus.TRIAL) {
          navigate(AppRoutes.trialRsoList);
        }
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      }
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RSO.FETCH_LIST] });
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
    setLoading(false);
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      await methods.handleSubmit(onSubmit)();
    },
  }));

  const { id: partnerId } = useParams();

  const backRoute = partnerId ? getDynamicRoute(AppRoutes.rsoDetail, { id: partnerId }) : AppRoutes.duringContractList;

  const name = useWatch({ control: methods.control, name: "name" });
  const displayName = name || "(登録支援機関名未入力)";

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={schema} initialValues={initialValues}>
      <MenuNavigate
        menuPrefix={
          <Typography variant="body14" sx={{ color: text.primary, px: 2, py: 1, fontWeight: 700 }}>
            {displayName}
          </Typography>
        }
        contents={[
          { title: "基本情報", content: <BasicForm /> },
          { title: "契約情報", content: <ContractForm /> },
          { title: "その他（顧客未開示の情報）", content: <OtherInfoForm /> },
        ]}
        contentWidth="900px"
        contentAffix={
          <FooterContainer sx={{ mt: 4 }}>
            <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
              <Button size="sm-round" variant="secondary" onClick={() => navigate(backRoute)}>
                戻る
              </Button>
              <Stack direction="row" gap={3}>
                <Button disabled={loading} size="sm-round" type="submit" startIcon={<SaveIcon />}>
                  保存
                </Button>
              </Stack>
            </Stack>
          </FooterContainer>
        }
      />
    </Form>
  );
});
